import { ReactComponent as OutlineStar } from "assets/svg/ic_round-star.svg";
import { ReactComponent as OutlineCheckSmall } from "assets/svg/outline-check-small.svg";
import { ReactComponent as OutlineChevronRight } from "assets/svg/outline-chevron-right-16.svg";
import React, { ReactNode, useMemo, useState } from "react";
import { Button, Card } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { AmplitudePageName, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import styles from "../Pricing.module.scss";
import {
  AI_ASSISTANT_BENEFITS,
  COMPLETE_BENEFITS,
  CORE_BENEFITS,
  ENABLED_PLAN_TYPES,
  ENTERPRISE_BENEFITS,
  PLAN_DISPLAY_NAMES,
  PLAN_PRICE_STRINGS,
} from "../constants";
import PlanModal from "./PlanModal/PlanModal";

type Props = {
  withSelect?: boolean;
  withPurchase?: boolean;
  withRestart?: boolean;
  pricing?: {
    annual: number;
    month: number;
  };
  sub?: any;
  withInfo?: boolean;
  subPeriod: string | undefined;
  amplitudePageName: AmplitudePageName;
  subClass: PlanType;
  startWithAddonSelected?: boolean;
};

const PlanCard = ({
  withSelect,
  withPurchase,
  withRestart,
  sub,
  withInfo = true,
  subPeriod,
  amplitudePageName,
  subClass,
  startWithAddonSelected,
}: Props) => {
  //TODO: delete
  //if (sub) sub.sub_class = "core-with-ai";

  const planDetails: {
    [plan in PlanType]: {
      name: string | ReactNode;
      benefits: string[];
      description: string;
      monthsFreeString?: string;
      bodyTitle: string;
      amplitudeComponentName: string;
      headerClass: string;
      selectPlanSearch: string;
      trialUrlPath?: string;
      purchasePath: string;
      withAddonPath?: string;
      selectPlanWithAddonSearch?: string;
      isMostPopular: boolean;
      subClass: string;
      subscriptionPeriodString?: string;
      ctaText: string;
      trialCtaText?: string;
      additionalCardClass?: string;
      mailTo?: string;
      limitedTimeOffer: boolean;
      changePlanCtaText: string;
    };
  } = {
    core: {
      name: PLAN_DISPLAY_NAMES[subClass],
      benefits: CORE_BENEFITS,
      description: "Everything solo practitioners need to get started.",
      monthsFreeString: "1 Month Free",
      bodyTitle: "Manage your practice with:",
      amplitudeComponentName: "core-plan",
      headerClass: "pricing__core-card-header",
      selectPlanSearch: `?class=core&period=${subPeriod}`,
      trialUrlPath: `/pricing/core/${subPeriod}/trial`,
      purchasePath: `/pricing/core/${subPeriod}/checkout`,
      withAddonPath: `/pricing/core-with-ai/${subPeriod}/checkout`,
      selectPlanWithAddonSearch: `?class=core-with-ai&period=${subPeriod}`,
      isMostPopular: false,
      subClass: "core",
      subscriptionPeriodString: "mo",
      ctaText: "Subscribe Now",
      trialCtaText: "Try for Free",
      limitedTimeOffer: true,
      changePlanCtaText: "Select this Plan",
    },
    complete: {
      name: PLAN_DISPLAY_NAMES[subClass],
      benefits: COMPLETE_BENEFITS,
      description: "Everything solo practitioners need to keep growing.",
      monthsFreeString: "2 Months Free",
      bodyTitle: "Get all the powers of Core, plus:",
      amplitudeComponentName: "complete-plan",
      headerClass: "pricing__complete-card-header",
      selectPlanSearch: `?class=complete&period=${subPeriod}`,
      purchasePath: `/pricing/complete/${subPeriod}/checkout`,
      isMostPopular: true,
      subClass: "complete",
      subscriptionPeriodString: "mo",
      ctaText: "Subscribe Now",
      limitedTimeOffer: true,
      changePlanCtaText: "Select this Plan",
    },
    "ai-only": {
      name: PLAN_DISPLAY_NAMES[subClass],
      benefits: AI_ASSISTANT_BENEFITS,
      description:
        "Basic plan for clinicians just getting started with generative AI.",
      monthsFreeString: "",
      bodyTitle: "",
      amplitudeComponentName: "ai-only-plan",
      headerClass: "pricing__core-card-header",
      selectPlanSearch: `?class=ai-only&period=${subPeriod}`,
      trialUrlPath: `/pricing/ai-only/${subPeriod}/trial`,
      purchasePath: `/pricing/ai-only/${subPeriod}/checkout`,
      isMostPopular: false,
      subClass: "ai-only",
      subscriptionPeriodString: "mo",
      ctaText: "Subscribe Now",
      trialCtaText: "Try for Free",
      limitedTimeOffer: true,
      changePlanCtaText: "Select this Plan",
    },
    enterprise: {
      name: PLAN_DISPLAY_NAMES[subClass],
      benefits: ENTERPRISE_BENEFITS,
      description:
        "Everything larger teams need to harness the power & flexibility of Orchid.",
      bodyTitle: "Get everything from Complete, plus:",
      amplitudeComponentName: "enterprise-plan",
      headerClass: "pricing__complete-card-header",
      selectPlanSearch: "#",
      purchasePath: "/contact-sales",
      isMostPopular: false,
      subClass: "enterprise",
      ctaText: "Contact Sales",
      additionalCardClass: styles["pricing__enterprise-card"],
      mailTo: "info@orchid.health",
      limitedTimeOffer: false,
      changePlanCtaText: "Contact Sales",
    },

    // Doesn't show up since it's just a checkbox in Core
    "core-with-ai": {
      name: PLAN_DISPLAY_NAMES[subClass],
      benefits: [],
      description: "",
      monthsFreeString: "",
      bodyTitle: "",
      amplitudeComponentName: "",
      headerClass: "",
      selectPlanSearch: "",
      purchasePath: "",
      isMostPopular: false,
      subClass: "core-with-ai",
      ctaText: "Try for Free",
      limitedTimeOffer: false,
      changePlanCtaText: "",
    },
  };

  const subIsLive = [
    // !!sub?.activated_time,
    !sub?.terminated_time,
    !sub?.terminated_code,
  ].every((e) => e === true);

  const { pathname } = useLocation();

  const showPlanSpecial = useMemo(() => {
    return (
      !pathname.startsWith("/pro-dashboard/subscription-settings") ||
      pathname.endsWith("/change-plan")
    );
  }, [pathname]);

  const [aiToolkitCheckboxChecked, setAiToolkitCheckboxChecked] = useState(
    startWithAddonSelected ||
      (subClass === "core" && sub?.sub_class === "core-with-ai"),
  );

  let isCurrentPlan;
  if (subClass === "core") {
    isCurrentPlan =
      subIsLive &&
      sub?.sub_period === subPeriod &&
      ((sub.sub_class === "core" && !aiToolkitCheckboxChecked) ||
        (sub.sub_class === "core-with-ai" && aiToolkitCheckboxChecked));
  } else {
    isCurrentPlan =
      subIsLive && sub?.sub_period === subPeriod && sub?.sub_class === subClass;
  }

  const [showPlanModal, setShowPlanModal] = useState(false);
  const planPriceComponent = (
    <div>
      <span className={styles["pricing__price"]}>
        {aiToolkitCheckboxChecked
          ? PLAN_PRICE_STRINGS["core-with-ai"]
          : PLAN_PRICE_STRINGS[subClass]}
      </span>
      {planDetails[subClass].subscriptionPeriodString && (
        <span className={getClasses("gray01", styles["pricing__price-suffix"])}>
          /{planDetails[subClass].subscriptionPeriodString}
        </span>
      )}
    </div>
  );

  const planDescription = (
    <>
      <div className={styles["pricing__description"]}>
        {planDetails[subClass].description}
      </div>
      {subPeriod !== "month" && (
        <div className={styles["pricing__tags"]}>
          {planDetails[subClass].monthsFreeString && (
            <span
              className={getClasses(
                styles["pricing__label-tag"],
                styles["pricing__blue-tag"],
              )}
            >
              {planDetails[subClass].monthsFreeString}
            </span>
          )}
          {isCurrentPlan && (
            <span
              className={getClasses(
                styles["pricing__label-tag"],
                styles["pricing__orange-tag"],
              )}
            >
              Current Plan
            </span>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      {showPlanModal && (
        <PlanModal
          onHide={() => setShowPlanModal(false)}
          amplitudePageName={amplitudePageName}
          amplitudeComponentName={planDetails["core"].amplitudeComponentName}
          changePlanSearch={planDetails["core"].selectPlanSearch}
          trialUrlPath={planDetails["core"].trialUrlPath!}
          purchasePath={planDetails["core"].purchasePath}
          purchaseWithAddonPath={planDetails["core"].withAddonPath!}
          changePlanWithAddonSearch={
            planDetails["core"].selectPlanWithAddonSearch
          }
          isCurrentPlan={false}
          subPeriod={subPeriod}
          withSelect={withSelect}
          currentSubClass={sub?.sub_class}
          subSuid={sub?.suid}
          currentSubPeriod={sub?.sub_period}
          planPriceComponent={planPriceComponent}
          planDescription={planDescription}
          aiToolkitCheckboxChecked={aiToolkitCheckboxChecked}
          setAiToolkitCheckboxChecked={setAiToolkitCheckboxChecked}
        />
      )}
      <Card
        className={getClasses(
          isCurrentPlan && styles["current-plan-border"],
          showPlanSpecial
            ? planDetails[subClass].isMostPopular
              ? `${styles["pricing__card"]} ${styles["pricing__card--most-popular"]}`
              : `${styles["pricing__card"]} ${styles["pricing__card--not-most-popular"]}`
            : styles["pricing__card"],
        )}
      >
        <div
          className={
            planDetails[subClass].isMostPopular && showPlanSpecial
              ? styles["pricing__most-popular-header"]
              : styles["pricing__top-spacer"]
          }
        >
          {planDetails[subClass].isMostPopular && showPlanSpecial ? (
            <div className={styles["pricing__most-popular-header"]}>
              <OutlineStar />
              Most Popular
            </div>
          ) : (
            ""
          )}
        </div>
        <div
          className={
            planDetails[subClass].additionalCardClass
              ? getClasses(
                  planDetails[subClass].additionalCardClass,
                  styles["pricing__card-content"],
                )
              : styles["pricing__card-content"]
          }
        >
          {showPlanSpecial && planDetails[subClass].limitedTimeOffer && (
            <div className={styles["pricing__card-banner"]}>
              Limited Time Offer
            </div>
          )}
          <div className={styles["pricing__card-title"]}>
            {planDetails[subClass].name}
          </div>

          <div className={getClasses("smoothed", styles["pricing__price-tag"])}>
            {planPriceComponent}
          </div>
          <div className={styles["pricing__card-body"]}>{planDescription}</div>
          <div className={styles["pricing__card-footer"]}>
            {withPurchase && (
              <>
                {isCurrentPlan ? (
                  <Button
                    variant="primary"
                    className={"w-100 bd-16 smoothed"}
                    size="lg"
                    disabled
                  >
                    {planDetails[subClass].ctaText}
                  </Button>
                ) : (
                  <Link
                    to={{
                      pathname: planDetails[subClass].trialUrlPath
                        ? aiToolkitCheckboxChecked
                          ? planDetails[subClass].withAddonPath
                          : subPeriod === "month"
                          ? planDetails[subClass].trialUrlPath
                          : planDetails[subClass].purchasePath
                        : planDetails[subClass].purchasePath,
                    }}
                  >
                    <Button
                      variant="primary"
                      size="lg"
                      className={"w-100 bd-16 smoothed"}
                      onClick={(e) => {
                        track(
                          amplitudePageName,
                          [
                            planDetails[subClass].amplitudeComponentName,
                            "start-trial-button",
                          ],
                          "click",
                          {
                            eventProperties: {
                              "time-period": subPeriod,
                            },
                          },
                        );
                        if (planDetails[subClass].mailTo) {
                          window.location.href = `mailto:${planDetails[subClass].mailTo}`;
                          e.preventDefault();
                        }
                      }}
                    >
                      {subPeriod === "month"
                        ? aiToolkitCheckboxChecked
                          ? planDetails[subClass].ctaText
                          : planDetails[subClass].trialCtaText ||
                            planDetails[subClass].ctaText
                        : planDetails[subClass].ctaText}
                    </Button>
                  </Link>
                )}
              </>
            )}
            {withSelect && (
              <>
                {isCurrentPlan ? (
                  <Button
                    variant="primary"
                    size="lg"
                    className={"w-100 bd-16"}
                    disabled
                  >
                    {planDetails[subClass].changePlanCtaText}
                  </Button>
                ) : (
                  <Link
                    to={`/pro-dashboard/subscription-settings/${sub.suid}/change${
                      aiToolkitCheckboxChecked
                        ? planDetails[subClass].selectPlanWithAddonSearch
                        : planDetails[subClass].selectPlanSearch
                    }`}
                  >
                    <Button
                      variant="primary"
                      size="lg"
                      className={"w-100 bd-16"}
                      onClick={(e) => {
                        track(
                          amplitudePageName,
                          [
                            planDetails[subClass].amplitudeComponentName,
                            "select-this-plan-button",
                          ],
                          "click",
                          {
                            eventProperties: {
                              "time-period": subPeriod,
                            },
                          },
                        );
                        if (planDetails[subClass].mailTo) {
                          window.location.href = `mailto:${planDetails[subClass].mailTo}`;
                          e.preventDefault();
                        }
                      }}
                    >
                      {planDetails[subClass].changePlanCtaText}
                    </Button>
                  </Link>
                )}
                <div className={styles["pricing__purchase-now"]}></div>
              </>
            )}
            {withRestart && (
              <>
                <Link
                  to={{ pathname: planDetails[subClass].purchasePath }}
                  onClick={(e) => {
                    if (planDetails[subClass].mailTo) {
                      window.location.href = `mailto:${planDetails[subClass].mailTo}`;
                      e.preventDefault();
                    }
                  }}
                >
                  <Button
                    variant="primary"
                    className={"w-100"}
                    onClick={() =>
                      track(
                        amplitudePageName,
                        [
                          planDetails[subClass].amplitudeComponentName,
                          "restart-subscription-button",
                        ],
                        "click",
                      )
                    }
                  >
                    Restart Subscription
                  </Button>
                </Link>
                <div className={styles["pricing__purchase-now"]}></div>
              </>
            )}
            {withInfo && (
              <>
                <p
                  className={getClasses(
                    "smoothed",
                    styles["pricing__card-body-title"],
                  )}
                >
                  {planDetails[subClass].bodyTitle}
                </p>

                <div>
                  {planDetails[subClass].benefits.map((benefit, index) => {
                    if (
                      benefit === "100% done-for-you client chart imports" &&
                      subPeriod === "month"
                    ) {
                      return null;
                    }
                    return (
                      <div key={index} className={styles["pricing__benefit"]}>
                        <OutlineCheckSmall />
                        {benefit}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {(withSelect || withPurchase) &&
              ENABLED_PLAN_TYPES.includes("core-with-ai") &&
              planDetails[subClass].withAddonPath && (
                <div
                  className={styles["pricing__addon-wrapper"]}
                  onClick={() => {
                    setShowPlanModal(true);
                  }}
                >
                  {/*<Checkbox
                    variant="rectangle"
                    label={"AI Toolkit"}
                    checked={aiToolkitCheckboxChecked}
                    readOnly
                    onClick={(e) => {
                      setAiToolkitCheckboxChecked(!aiToolkitCheckboxChecked);
                      e.stopPropagation();
                    }}
                  />*/}
                  <div>AI Toolkit</div>
                  <div className={styles["pricing__addon-price"]}>
                    <div>
                      {sub?.sub_class === "core-with-ai" &&
                      sub?.sub_period === subPeriod
                        ? "Added for +$9.98/mo"
                        : "Add $9.98/mo"}
                    </div>
                    <OutlineChevronRight />
                  </div>
                </div>
              )}
          </div>
        </div>
      </Card>
    </>
  );
};

export default PlanCard;
